import React, { useEffect, useState } from "react";
import { Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Spinner from "../Common/Spinner";
import { CommonService } from "../../Service/CommonService";
import { Service } from "../../Service/ApiService";
import { useHistory, useParams, useLocation } from "react-router";
import InputMask from "react-input-mask";
import { Prev } from "react-bootstrap/lib/Pagination";
import { ErrorSharp, Preview } from "@mui/icons-material";

const AddOwner = () => {
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [formData, setFormData] = useState({
    id: id ? id : "",
    companyId: CommonService.isUser() ? CommonService.getCompanyId() : "",
    ownerName: "",
    location: null,
    email: null,
    phone: null,
  });

  const fetchOwnerDetails = async () => {
    try {
      const response = await Service.getBarrelOwnerDetails(id);
      setFormData(response.data);
      setOriginalData(response.data); // save original
    } catch (error) {
      console.log("Error occurred while api calling " + error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOwnerDetails();
    }
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Remove error for this select field
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    let updatedFormData = {
      ...formData,
      [name]: value,
    };

    setFormData(updatedFormData);
  };

  const validateOwnerDetails = () => {
    let isValid = true;
    let newErrors = { ...errors };

    // Required field validations
    if (!formData.companyId.trim()) {
      newErrors.companyId = "Company is required";
      isValid = false;
    }

    if (!formData.ownerName.trim()) {
      newErrors.ownerName = "Owner name is required";
      isValid = false;
    }

    if (formData.phone) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(formData.phone) || formData.phone.length < 10) {
        newErrors.phone = "Please enter a valid phone number";
        isValid = false;
      }
    }

    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
        isValid = false;
      }
    }

    // as of now we don't need to add the validation on location (3 July, 2025)
    // if (!formData.location.trim()) {
    //   newErrors.location = "Location is required";
    //   isValid = false;
    // }

    // Set errors
    setErrors(newErrors);

    return isValid;
  };

  const handleOwnerSubmit = async (event: any) => {
    event.preventDefault();

    //check validation
    if (!(await validateOwnerDetails())) {
      return;
    }

    //check input data update or not
    if (id && JSON.stringify(formData) === JSON.stringify(originalData)) {
      CommonService.Toast.fire({
        title: "No changes detected. Nothing to update.",
        icon: "info",
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await Service.saveBarrelOwner(formData);
      CommonService.Toast.fire({
        title: id
          ? "Owner Details Updated Successfully"
          : "Owner Details Submitted Successfully",
        icon: "success",
      });
      //redirect to the listing page
      if (
        history.action === "PUSH" ||
        history.action === "REPLACE" ||
        (location.state && (location.state as any).fromInternal)
      ) {
        history.goBack();
      } else {
        history.replace("/user/barrel-owners");
      }
    } catch (error) {
      console.log("Error occurred while saving the owners");
      if(error.response.data.errorMessage) {
        setErrors(prev => ({
          ...prev,
          email: error.response.data.errorMessage,
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const routeBack = () => {
    // Handle back navigation
    if (
      history.action === "PUSH" ||
      history.action === "REPLACE" ||
      (location.state && (location.state as any).fromInternal)
    ) {
      history.goBack();
    } else {
      history.replace("/user/barrel-owners");
    }
  };

  return (
    <>
      <div className="add-company-user-wrapper">
        <div className="d-flex align-items-center mb-3 px-3">
          {CommonService.isAdmin() && (
            <>
              <div className="back__button" onClick={routeBack}>
                <IconButton
                  aria-label="ArrowBackIcon"
                  sx={{ backgroundColor: "#FFF" }}
                >
                  <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                </IconButton>
              </div>
              <div className="title">
                <Typography
                  variant="h1"
                  component="h2"
                  sx={{ ml: 2, fontWeight: 600, fontSize: "25px" }}
                >
                  {id ? "Update Owner Details" : "Add New Owner"}
                </Typography>
              </div>
            </>
          )}
        </div>

        <div className="add-company-user-form-wrapper p-0">
          <form className="form__inner row d-flex mx-auto py-5">
            <div className="col-12 col-sm-6 mb-3 form-group-wrapper">
              <label htmlFor="ownerName" className="form-label">
                Owner Name <span className="star-required text-danger">*</span>
              </label>
              <input
                type="text"
                id="ownerName"
                name="ownerName"
                placeholder="Enter Owner Name"
                value={formData.ownerName}
                onChange={handleChange}
                className={`form-control add-contract-input ${
                  errors.ownerName ? "is_invalid" : ""
                }`}
              />
              {errors.ownerName && (
                <div className="error-message">{errors.ownerName}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 mb-3 form-group-wrapper">
              <label htmlFor="email" className="form-label">
                Email Address{" "}
              </label>
              <input
                type="text"
                id="email"
                name="email"
                placeholder="Enter Owner Email Address"
                value={formData.email}
                onChange={handleChange}
                className={`form-control add-contract-input ${
                  errors.email ? "is_invalid" : ""
                }`}
              />
              {errors.email && (
                <div className="error-message">{errors.email}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 mb-3 form-group-wrapper">
              <label htmlFor="phone" className="form-label">
                Phone Number{" "}
              </label>
              <InputMask
                mask="(999) 999-9999"
                value={formData.phone}
                onChange={handleChange}
              >
                {(inputProps) => (
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    placeholder="Enter Owner Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-control add-contract-input ${
                      errors.phone ? "is_invalid" : ""
                    }`}
                    {...inputProps}
                  />
                )}
              </InputMask>
              {errors.phone && (
                <div className="error-message">{errors.phone}</div>
              )}
            </div>

            <div className="col-12 col-sm-6 mb-3 form-group-wrapper">
              <label htmlFor="location" className="form-label">
                Owner Address{" "}
              </label>
              <input
                type="text"
                id="location"
                name="location"
                placeholder="Enter Owner Location"
                value={formData.location}
                onChange={handleChange}
                className={`form-control add-contract-input ${
                  errors.location ? "is_invalid" : ""
                }`}
              />
              {errors.location && (
                <div className="error-message">{errors.location}</div>
              )}
            </div>

            {/* Submit Button */}
            <div className="col-12 d-flex justify-content-center mt-4 gap-4">
              <button
                type="reset"
                onClick={routeBack}
                className="btn secondary-btn register-btn mb-2"
                disabled={isLoading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn primary-btn register-btn mb-2"
                onClick={handleOwnerSubmit}
                disabled={isLoading}
              >
                {isLoading && <Spinner />} {id ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddOwner;
